export interface StrokeGlyph {
    strokes: string[];
    medians: number[][][];
}

export type GlyphMap = Record<string, StrokeGlyph>;

const cache = new Map<string, Promise<StrokeGlyph | null>>();

export function strokeDataUrl(char: string): string {
    const codepoint = (char.codePointAt(0) ?? 0).toString(16);
    return `${process.env.PUBLIC_URL || ""}/stroke-data/${codepoint}.json`;
}

function isStrokeGlyph(value: unknown): value is StrokeGlyph {
    const data = value as StrokeGlyph;
    return Boolean(data) && Array.isArray(data.strokes) && Array.isArray(data.medians);
}

export function loadStrokeGlyph(char: string): Promise<StrokeGlyph | null> {
    const cached = cache.get(char);
    if (cached) return cached;
    const pending = fetch(strokeDataUrl(char))
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => (isStrokeGlyph(data) ? data : null))
        .catch(() => {
            cache.delete(char);
            return null;
        });
    cache.set(char, pending);
    return pending;
}

export async function loadStrokeGlyphs(chars: readonly string[], concurrency = 8): Promise<GlyphMap> {
    const unique = Array.from(new Set(chars));
    const result: GlyphMap = {};
    let index = 0;
    async function worker() {
        while (index < unique.length) {
            const char = unique[index];
            index += 1;
            const glyph = await loadStrokeGlyph(char);
            if (glyph) result[char] = glyph;
        }
    }
    await Promise.all(Array.from({ length: Math.min(concurrency, unique.length) }, worker));
    return result;
}

export function clearStrokeGlyphCache(): void {
    cache.clear();
}

type Segment =
    | { op: "M" | "L"; points: [number, number] }
    | { op: "C"; points: [number, number, number, number, number, number] }
    | { op: "Z" };

export function parseSvgPath(path: string): Segment[] {
    const tokens = path.match(/[MLQCZmlqcz]|-?\d*\.?\d+(?:e[-+]?\d+)?/g) ?? [];
    const segments: Segment[] = [];
    let command = "";
    let i = 0;
    let current: [number, number] = [0, 0];
    let start: [number, number] = [0, 0];
    const next = () => Number(tokens[i++]);
    while (i < tokens.length) {
        if (/^[A-Za-z]$/.test(tokens[i])) {
            command = tokens[i].toUpperCase();
            i += 1;
            if (command === "Z") {
                segments.push({ op: "Z" });
                current = start;
                continue;
            }
        }
        if (command === "M" || command === "L") {
            const point: [number, number] = [next(), next()];
            segments.push({ op: command, points: point });
            current = point;
            if (command === "M") {
                start = point;
                command = "L";
            }
        } else if (command === "C") {
            const points: [number, number, number, number, number, number] = [next(), next(), next(), next(), next(), next()];
            segments.push({ op: "C", points });
            current = [points[4], points[5]];
        } else if (command === "Q") {
            const qx = next();
            const qy = next();
            const x = next();
            const y = next();
            segments.push({
                op: "C",
                points: [
                    current[0] + (2 / 3) * (qx - current[0]),
                    current[1] + (2 / 3) * (qy - current[1]),
                    x + (2 / 3) * (qx - x),
                    y + (2 / 3) * (qy - y),
                    x,
                    y,
                ],
            });
            current = [x, y];
        } else {
            i += 1;
        }
    }
    return segments;
}

function fmt(value: number): string {
    return String(Math.round(value * 10) / 10);
}

export function svgPathToPdfOps(path: string): string {
    return parseSvgPath(path)
        .map((segment) => {
            if (segment.op === "Z") return "h";
            if (segment.op === "C") return `${segment.points.map(fmt).join(" ")} c`;
            return `${segment.points.map(fmt).join(" ")} ${segment.op === "M" ? "m" : "l"}`;
        })
        .join("\n");
}
